// Wire-shape requests for /articles/* endpoints.
import { z } from 'zod';
import { ArticleAccessMode, ArticleBody, ArticleCollaboratorRole, ArticleCollaboratorTargetType, ArticleHttpUrl, ArticleSeo, ArticleWechatThemeDefinition, } from './article.js';
const uniqueUuidArray = (max) => z
    .array(z.string().uuid())
    .max(max)
    .refine((ids) => new Set(ids).size === ids.length, 'ids must be unique');
// ---------------------------------------------------------------------------
// CreateArticleRequest — POST /articles.
//
// `slug` is OPTIONAL. When omitted (or empty), the service derives one
// from the title via the pinyin → ASCII → slugify pipeline (gated by
// the `articles.slug-pinyin-fallback` flag, default ON), then resolves
// uniqueness with -2..-5 suffix retries before falling back to a
// random hex suffix. When the flag is OFF, the create route requires
// an explicit slug as before (422 if missing).
//
// All visibility / SEO fields are optional — defaults match the schema:
// access_mode='public', no SEO overrides, no scheduled times.
// ---------------------------------------------------------------------------
export const CreateArticleRequest = z.object({
    slug: z
        .string()
        .min(1)
        .max(96)
        .regex(/^[a-z0-9](-?[a-z0-9])*$/, 'slug must be lowercase letters/digits with single hyphens')
        .optional(),
    title: z.string().min(1).max(280),
    summary: z.string().max(2000).optional(),
    body: ArticleBody.optional(),
    coverImageUrl: ArticleHttpUrl.nullable().optional(),
    accessMode: ArticleAccessMode.optional(),
    password: z.string().min(1).max(256).nullable().optional(),
    paywallAmountCents: z.number().int().min(1).max(100_000_000).nullable().optional(),
    paywallCurrency: z.string().regex(/^[A-Z]{3}$/).nullable().optional(),
    allowlistUserIds: uniqueUuidArray(256).optional(),
    /** Group ids to attach on create. Ignored unless `accessMode='group'`. */
    groupIds: uniqueUuidArray(64).optional(),
    /** Optional taxonomy attachment. Validated to exist + not be archived. */
    categoryId: z.string().uuid().nullable().optional(),
    /** Optional tag attachments. Each id must exist + not be archived. */
    tagIds: uniqueUuidArray(32).optional(),
    /** Optional auto-unpublish deadline. Must be strictly in the future
     *  when set. Honoured by the `articles.scheduled-unpublish` cron once
     *  the article reaches `status='published'` (the cron filters
     *  `status='published'` so values set on drafts simply wait until
     *  publish). Send `null` to clear. */
    scheduledUnpublishAt: z.string().datetime({ offset: true }).nullable().optional(),
    /** Partial SEO overrides — fields omitted use schema defaults. */
    seo: ArticleSeo.partial().optional(),
});
// ---------------------------------------------------------------------------
// UpdateArticleRequest — PATCH /articles/:slug.
//
// Slug is NOT mutable in V1 (renaming articles is a follow-up PR — needs
// a redirect-shim row to avoid breaking permalinks). Status transitions
// happen via the dedicated /publish, /unpublish, DELETE routes.
//
// Note: optimistic-concurrency (`expectedUpdatedAt`) is a V2 surface and
// is intentionally absent from this shape — adding a field to the
// contract that the route would silently strip drifts the SDK away from
// reality. Re-introduce here at the same time the route schema and
// service layer plumb it through.
// ---------------------------------------------------------------------------
export const UpdateArticleRequest = CreateArticleRequest.omit({ slug: true }).partial();
export const UpdateArticleCollaborationRequest = z.object({
    enabled: z.boolean(),
});
export const CreateArticleCollaborationTicketRequest = z.object({
    clientSessionId: z.string().min(1).max(128).optional(),
});
export const UpsertArticleCollaboratorRequest = z.object({
    targetType: ArticleCollaboratorTargetType,
    targetId: z.string().uuid(),
    role: ArticleCollaboratorRole,
});
export const UpdateArticleCollaboratorRequest = z.object({
    role: ArticleCollaboratorRole,
});
export const CreateArticleWechatThemeRequest = z.object({
    name: z.string().trim().min(1).max(80),
    description: z.string().max(500).optional(),
    definition: ArticleWechatThemeDefinition,
});
export const UpdateArticleWechatThemeRequest = CreateArticleWechatThemeRequest.partial();
// ---------------------------------------------------------------------------
// PublishArticleRequest — POST /articles/:slug/publish.
//
// Body is optional. When `at` is omitted the article goes live immediately
// and emits `article.published`. When `at` is in the future the article
// enters `scheduled` state and emits `article.scheduled`; past or invalid
// `at` values are rejected with ARTICLE_SCHEDULED_IN_PAST.
//
// `.nullable()` mirrors the route schema in
// `apps/api/src/modules/content/articles/route-schemas.ts`. Fastify
// materialises a missing JSON body as `null`, so an SDK that wants to
// "publish now" can either send `{}`, `{ at }`, or omit the body.
// ---------------------------------------------------------------------------
export const PublishArticleRequest = z
    .object({
    at: z.string().datetime({ offset: true }).optional(),
})
    .nullable();
// Empty bodies — keep the type around so handlers can type-narrow.
export const UnpublishArticleRequest = z.object({}).strict();
// ---------------------------------------------------------------------------
// AttachGroupsRequest — POST /articles/:slug/groups.
// One group per call to keep the activity stream's payload simple.
// ---------------------------------------------------------------------------
export const AttachGroupRequest = z.object({
    groupId: z.string().uuid(),
});
export const UnlockArticleRequest = z.object({
    password: z.string().min(1).max(256),
});
export const PurchaseArticleRequest = z.object({}).strict().nullable();
// ---------------------------------------------------------------------------
// ListArticlesQuery — GET /articles.
//
// `mine=true` filters to viewer-authored articles regardless of status
// (so authors see drafts in their own list). Without it, only viewer-
// readable articles surface (canRead-filtered).
// ---------------------------------------------------------------------------
export const ListArticlesQuery = z.object({
    scope: z.enum(['browse', 'mine', 'collaborating']).optional(),
    mine: z.boolean().optional(),
    status: z
        .enum(['draft', 'scheduled', 'published', 'unpublished', 'archived'])
        .optional(),
    groupId: z.string().uuid().optional(),
    /** Restrict to articles attached to this category (flat match — a
     *  future hierarchical "include descendants" mode is not part of V1).
     *  The route short-circuits to an empty list when the category is
     *  archived or missing, to avoid leaking taxonomy existence by count. */
    categoryId: z.string().uuid().optional(),
    /** Restrict to articles tagged with this tag id. Same archived /
     *  missing short-circuit as `categoryId`. */
    tagId: z.string().uuid().optional(),
    q: z.string().min(0).max(128).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    // `offset` is fenced at 100 000 to match the TypeBox route schema.
    // Beyond that the underlying SQL `OFFSET` is wasteful (PostgreSQL
    // still scans skipped rows) and clients should switch to keyset
    // pagination — but that's a V2 surface.
    offset: z.number().int().min(0).max(100_000).optional(),
});
// ---------------------------------------------------------------------------
// Reading-layer query shapes — `prev`, `next`, `related`.
//
// `scope` selects whether prev/next walks within the article's category
// (default) or globally over all public-published articles. The route
// layer pre-filters at SQL to `status='published' AND access_mode='public'`
// in both cases, so crawlers and unauthenticated viewers see deterministic
// results.
// ---------------------------------------------------------------------------
export const ReadingScopeQuery = z.object({
    scope: z.enum(['category', 'global']).optional(),
});
export const RelatedQuery = z.object({
    /** 1..20; the route caps at 20 regardless of input. */
    limit: z.number().int().min(1).max(20).optional(),
});
export const ArticleReadCompletionBody = z.object({
    progress: z.number().int().min(0).max(100).optional(),
    dwellSeconds: z.number().int().min(0).optional(),
});
//# sourceMappingURL=requests.js.map